/**
 * Accounts Matcher API — Instant Funding pair calculator + live dashboard.
 *
 *   GET  /api/accounts-matcher/if-accounts   list owned accounts where
 *                                            challenge_type = 'instant'
 *   GET  /api/accounts-matcher/if-pairs      list active account pairs
 *                                            where ≥1 leg is instant
 *   POST /api/accounts-matcher/calculate     run derivePairRisk on raw
 *                                            inputs (calculator mode)
 *
 * Live state (current_balance / current_equity / biggest_winning_day /
 * total_profit / today_realised_pnl) lives in the per-pair HedgePair DO
 * sqlite, not D1. v1 returns nullable fields for those; the dashboard
 * gracefully shows "—" until a follow-up PR pipes the DO snapshots back
 * to D1.
 */

import { z } from "zod";

export const AccountRiskConfigZ = z.object({
  startingBalance: z.number(),
  currentBalance: z.number(),
  currentEquity: z.number(),
  maxFloatingPct: z.number().nullable(),
  dailyDdPct: z.number(),
  lifetimeDdPct: z.number(),
  consistencyPct: z.number().nullable(),
  startOfDayEquity: z.number(),
  todayRealisedPnl: z.number(),
  biggestWinningDayUsd: z.number(),
  totalProfitUsd: z.number(),
});
export type AccountRiskConfigDTO = z.infer<typeof AccountRiskConfigZ>;

export const DerivedAccountRiskZ = z.object({
  perTradeSlMaxUsd: z.number().nullable(),
  dailyDdRemainingUsd: z.number(),
  lifetimeDdRemainingUsd: z.number(),
  consistencyRatio: z.number(),
  withdrawableNow: z.boolean(),
  maxSafeTodayRealisedUsd: z.number(),
  lifetimeSlHitBudget: z.number(),
});
export type DerivedAccountRiskDTO = z.infer<typeof DerivedAccountRiskZ>;

export const PairDerivedRiskZ = z.object({
  perTradeSlMaxUsd: z.number(),
  dailyTargetUsd: z.number(),
  daysToFirstDualWithdrawal: z.number(),
  bindingLeg: z.enum(["A", "B", "tie"]),
  lifetimeSlHitBudget: z.number(),
  perLeg: z.object({ A: DerivedAccountRiskZ, B: DerivedAccountRiskZ }),
  notes: z.array(z.string()),
});
export type PairDerivedRiskDTO = z.infer<typeof PairDerivedRiskZ>;

/** Owned-IF-account row, one per account. */
export const IfAccountSummaryZ = z.object({
  id: z.string(),
  slug: z.string(),
  firmId: z.string(),
  firmName: z.string(),
  firmLogo: z.string().nullable(),
  login: z.string().nullable(),
  accountLabel: z.string().nullable(),
  phase: z.string().nullable(),
  challengeType: z.string().nullable(),
  /** Slug of the partner account if currently paired, else null. */
  pairedWithSlug: z.string().nullable(),
  /** Slug of the account_pairs row if paired, else null. */
  accountPairSlug: z.string().nullable(),
  risk: AccountRiskConfigZ,
  derived: DerivedAccountRiskZ,
});
export type IfAccountSummary = z.infer<typeof IfAccountSummaryZ>;

export const ListIfAccountsResZ = z.object({
  accounts: z.array(IfAccountSummaryZ),
});
export type ListIfAccountsRes = z.infer<typeof ListIfAccountsResZ>;

export const IfPairSummaryZ = z.object({
  pairSlug: z.string(),
  legA: IfAccountSummaryZ,
  legB: IfAccountSummaryZ,
  derived: PairDerivedRiskZ,
});
export type IfPairSummary = z.infer<typeof IfPairSummaryZ>;

export const ListIfPairsResZ = z.object({
  pairs: z.array(IfPairSummaryZ),
});
export type ListIfPairsRes = z.infer<typeof ListIfPairsResZ>;

export const CalculateReqZ = z.object({
  legA: AccountRiskConfigZ,
  legB: AccountRiskConfigZ,
  slippageHeadroomPct: z.number().min(0).max(0.95).optional(),
});
export type CalculateReq = z.infer<typeof CalculateReqZ>;

export const CalculateResZ = z.object({
  derived: PairDerivedRiskZ,
});
export type CalculateRes = z.infer<typeof CalculateResZ>;
